import {
  ChangeDetectionStrategy,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  inject,
  OnDestroy,
  signal,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  ClientRepresentation,
  KeycloakAdminApi,
  UserRepresentation,
} from '../../services/keycloak-admin-api';

@Component({
  selector: 'app-create-client',
  imports: [RouterLink, ReactiveFormsModule],
  templateUrl: './create-client.html',
  styleUrl: './create-client.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  // eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class CreateClient implements OnDestroy {
  private keycloakApi = inject(KeycloakAdminApi);

  // Phase 2: Form state management
  clientMode = signal<'create' | 'select'>('create');
  isLoading = signal(false);
  searchResults = signal<ClientRepresentation[]>([]);
  selectedClient = signal<ClientRepresentation | null>(null);

  // Phase 3: User selection state management
  currentStep = signal<'client' | 'users' | 'summary'>('client');
  currentStepIndex = computed(() => {
    switch (this.currentStep()) {
      case 'client':
        return 1;
      case 'users':
        return 2;
      case 'summary':
        return 3;
    }
  });
  userSearchResults = signal<UserRepresentation[]>([]);
  isLoadingUsers = signal(false);
  selectedDevelopers = signal<UserRepresentation[]>([]);
  selectedUserAdmins = signal<UserRepresentation[]>([]);

  // Phase 4: Submission state management
  isSubmitting = signal(false);
  submissionError = signal<string | null>(null);
  submissionSuccess = signal(false);
  submissionStep = signal<
    'client' | 'permissions' | 'attributes' | 'policies' | 'permissions' | 'complete'
  >('client');
  submissionProgress = signal(0);

  // Phase 10: Enhanced error handling and user feedback
  isValidating = signal(false);
  validationErrors = signal<string[]>([]);
  networkError = signal<string | null>(null);
  retryCount = signal(0);
  maxRetries = 3;

  // Computed signal for current search term
  currentSearchTerm = computed(() => this.searchForm.get('selectedClientValue')?.value || '');

  // Form for creating new client with enhanced validation
  createClientForm = new FormGroup({
    clientId: new FormControl('', [
      Validators.required,
      Validators.minLength(2),
      Validators.maxLength(50),
      Validators.pattern(/^[a-zA-Z0-9_-]+$/),
    ]),
    name: new FormControl('', [
      Validators.required,
      Validators.minLength(2),
      Validators.maxLength(50),
    ]),
    description: new FormControl('', [Validators.maxLength(255)]),
    protocol: new FormControl('openid-connect', [Validators.required]),
  });

  // Form for searching existing clients
  searchForm = new FormGroup({
    searchTerm: new FormControl(''),
    selectedClientValue: new FormControl(''),
  });

  // Form for searching users
  userSearchForm = new FormGroup({
    searchTerm: new FormControl(''),
  });

  constructor() {
    // Initialize component
  }

  ngOnDestroy() {
    // Clean up timeouts to prevent memory leaks
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
    if (this.userSearchTimeout) {
      clearTimeout(this.userSearchTimeout);
    }
  }

  // Toggle between create and select modes
  setClientMode(tabEvent: Event) {
    const activeTab = (tabEvent as CustomEvent)?.detail?.activeTab;
    this.clientMode.set(activeTab);
    this.selectedClient.set(null);
    this.searchResults.set([]);
    this.createClientForm.reset({
      clientId: '',
      name: '',
      description: '',
      protocol: 'openid-connect',
    });
    this.searchForm.reset({
      searchTerm: '',
      selectedClientValue: '',
    });
  }

  // Enhanced search for existing clients with debouncing for ep-combobox
  private searchTimeout: ReturnType<typeof setTimeout> | null = null;

  async searchClients(event?: Event) {
    // Clear previous timeout
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }

    // Get search term from the input event or form control
    const searchTerm =
      (event as CustomEvent)?.detail?.query ||
      this.searchForm.get('selectedClientValue')?.value ||
      '';

    if (!searchTerm || searchTerm.trim().length < 2) {
      this.searchResults.set([]);
      return;
    }

    // Debounce search by 300ms
    this.isLoading.set(true);
    try {
      const allClients = await this.handleApiCall(
        () => this.keycloakApi.getClients(),
        'Failed to search clients',
        false // Don't retry client search failures
      );

      const searchTermLower = searchTerm.toLowerCase().trim();

      const filtered = allClients.filter(
        (client) =>
          client.name?.toLowerCase().includes(searchTermLower) ||
          client.clientId?.toLowerCase().includes(searchTermLower) ||
          client.id?.toLowerCase().includes(searchTermLower) ||
          client.description?.toLowerCase().includes(searchTermLower)
      );

      const limitedResults = filtered.slice(0, 20); // Limit to 20 results for performance
      this.searchResults.set(limitedResults);
    } catch (error) {
      console.error('Error searching clients:', error);
      this.searchResults.set([]);
    } finally {
      this.isLoading.set(false);
    }

    // Update combobox display after results are set
    setTimeout(() => {
      (event?.target as any)?.updateOptionsDisplay();
    }, 10);
  }

  // Handle client selection from ep-combobox
  onClientSelection(event: Event) {
    const selectedValue = (event as CustomEvent)?.detail?.value || (event?.target as any)?.value;

    if (selectedValue) {
      const client = this.searchResults().find(
        (c) => c.id === selectedValue || c.clientId === selectedValue
      );
      if (client) {
        this.selectClient(client);
      }
    } else {
      // If no value, clear selection
      this.selectedClient.set(null);
    }
  }

  // Select an existing client
  selectClient(client: ClientRepresentation) {
    this.selectedClient.set(client);
  }

  // Clear selected client
  clearSelection() {
    this.selectedClient.set(null);
    this.searchForm.get('selectedClientValue')?.setValue('');
    this.searchResults.set([]); // Clear search results when clearing selection
  }

  // Enhanced user search functionality with debouncing
  private userSearchTimeout: ReturnType<typeof setTimeout> | null = null;

  async searchUsers() {
    // Clear previous timeout
    if (this.userSearchTimeout) {
      clearTimeout(this.userSearchTimeout);
    }

    const searchTerm = this.userSearchForm.get('searchTerm')?.value;
    if (!searchTerm || searchTerm.trim().length < 2) {
      this.userSearchResults.set([]);
      return;
    }

    // Debounce search by 300ms
    this.userSearchTimeout = setTimeout(async () => {
      this.isLoadingUsers.set(true);
      try {
        const users = await this.handleApiCall(
          () => this.keycloakApi.getUsers({ search: searchTerm.trim() }, 0, 50),
          'Failed to search users',
          false // Don't retry user search failures
        );

        // Filter out users that are already selected to avoid duplicates in search results
        const alreadySelectedIds = new Set([
          ...this.selectedDevelopers().map((u) => u.id),
          ...this.selectedUserAdmins().map((u) => u.id),
        ]);

        const filteredUsers = users.filter((user) => !alreadySelectedIds.has(user.id));
        this.userSearchResults.set(filteredUsers);
      } catch (error) {
        console.error('Error searching users:', error);
        this.userSearchResults.set([]);
      } finally {
        this.isLoadingUsers.set(false);
      }
    }, 300);
  }

  // Add user as developer
  addDeveloper(user: UserRepresentation) {
    const currentDevelopers = this.selectedDevelopers();
    const currentUserAdmins = this.selectedUserAdmins();

    // Remove from user admins if already there
    if (currentUserAdmins.find((u) => u.id === user.id)) {
      this.selectedUserAdmins.set(currentUserAdmins.filter((u) => u.id !== user.id));
    }

    // Add to developers if not already there
    if (!currentDevelopers.find((u) => u.id === user.id)) {
      this.selectedDevelopers.set([...currentDevelopers, user]);
    }
  }

  // Add user as user admin
  addUserAdmin(user: UserRepresentation) {
    const currentDevelopers = this.selectedDevelopers();
    const currentUserAdmins = this.selectedUserAdmins();

    // Remove from developers if already there
    if (currentDevelopers.find((u) => u.id === user.id)) {
      this.selectedDevelopers.set(currentDevelopers.filter((u) => u.id !== user.id));
    }

    // Add to user admins if not already there
    if (!currentUserAdmins.find((u) => u.id === user.id)) {
      this.selectedUserAdmins.set([...currentUserAdmins, user]);
    }
  }

  // Remove user from developers
  removeDeveloper(user: UserRepresentation) {
    const currentDevelopers = this.selectedDevelopers();
    this.selectedDevelopers.set(currentDevelopers.filter((u) => u.id !== user.id));
  }

  // Remove user from user admins
  removeUserAdmin(user: UserRepresentation) {
    const currentUserAdmins = this.selectedUserAdmins();
    this.selectedUserAdmins.set(currentUserAdmins.filter((u) => u.id !== user.id));
  }

  // Check if user is already assigned to any role
  getUserRole(user: UserRepresentation): 'developer' | 'user-admin' | null {
    if (this.selectedDevelopers().find((u) => u.id === user.id)) return 'developer';
    if (this.selectedUserAdmins().find((u) => u.id === user.id)) return 'user-admin';
    return null;
  }

  // Navigation between steps
  goToStep(step: 'client' | 'users' | 'summary') {
    this.currentStep.set(step);
  }

  // Check if form is valid and ready to continue
  canContinue(): boolean {
    if (this.clientMode() === 'create') {
      return this.createClientForm.valid;
    } else {
      return this.selectedClient() !== null;
    }
  }

  // Get form validation error messages with enhanced feedback
  getFieldError(fieldName: string): string {
    const field = this.createClientForm.get(fieldName);
    if (field?.errors && field.touched) {
      if (field.errors['required']) return `${this.capitalize(fieldName)} is required`;
      if (field.errors['minlength'])
        return `${this.capitalize(fieldName)} must be at least ${
          field.errors['minlength'].requiredLength
        } characters`;
      if (field.errors['maxlength'])
        return `${this.capitalize(fieldName)} cannot exceed ${
          field.errors['maxlength'].requiredLength
        } characters`;
      if (field.errors['pattern'])
        return `${this.capitalize(fieldName)} contains invalid characters`;
      if (field.errors['email']) return `Please enter a valid email address`;
    }
    return '';
  }

  // Helper method to capitalize field names
  private capitalize(text: string): string {
    return text.charAt(0).toUpperCase() + text.slice(1);
  }

  // Enhanced form validation - context-aware based on current step
  private validateForm(): string[] {
    const errors: string[] = [];

    // Always validate client setup if we're in create mode or if no client is selected
    if (this.clientMode() === 'create') {
      const clientId = this.createClientForm.get('clientId')?.value;
      const name = this.createClientForm.get('name')?.value;
      if (!clientId || clientId.trim().length < 2) {
        errors.push('Client ID must be at least 2 characters long');
      }
      if (clientId && clientId.length > 50) {
        errors.push('Client ID cannot exceed 50 characters');
      }
      if (clientId && !/^[a-zA-Z0-9_-]+$/.test(clientId.trim())) {
        errors.push('Client ID can only contain letters, numbers, hyphens, and underscores');
      }
      if (!name || name.trim().length < 2) {
        errors.push('Client name must be at least 2 characters long');
      }
      if (name && name.length > 50) {
        errors.push('Client name cannot exceed 50 characters');
      }
    } else if (!this.selectedClient()) {
      errors.push('Please select a client');
    }

    // Only validate user assignments when we're at the summary step or beyond
    if (this.currentStep() === 'summary') {
      if (this.selectedDevelopers().length === 0 && this.selectedUserAdmins().length === 0) {
        errors.push('Please assign at least one developer or user admin');
      }
    }

    return errors;
  }

  // Network error handling with retry logic
  private async handleApiCall<T>(
    apiCall: () => Promise<T>,
    errorContext: string,
    retryable = true
  ): Promise<T> {
    try {
      return await apiCall();
    } catch (error) {
      console.error(`${errorContext}:`, error);

      if (retryable && this.retryCount() < this.maxRetries) {
        this.retryCount.set(this.retryCount() + 1);
        console.log(`Retrying ${errorContext} (attempt ${this.retryCount()}/${this.maxRetries})`);

        // Exponential backoff
        await new Promise((resolve) => setTimeout(resolve, Math.pow(2, this.retryCount()) * 1000));

        return this.handleApiCall(apiCall, errorContext, retryable);
      }

      this.networkError.set(
        `${errorContext}: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
      throw error;
    }
  }

  // Clear all error states
  private clearErrors(): void {
    this.submissionError.set(null);
    this.validationErrors.set([]);
    this.networkError.set(null);
    this.retryCount.set(0);
  }

  // Continue to next phase with enhanced validation
  continue() {
    this.clearErrors();
    this.isValidating.set(true);

    const errors = this.validateForm();
    if (errors.length > 0) {
      this.validationErrors.set(errors);
      this.isValidating.set(false);
      return;
    }

    this.isValidating.set(false);

    if (this.currentStep() === 'client') {
      this.goToStep('users');
    } else if (this.currentStep() === 'users') {
      this.goToStep('summary');
    }
  }

  // Go back to previous step
  goBack() {
    if (this.currentStep() === 'users') {
      this.goToStep('client');
    } else if (this.currentStep() === 'summary') {
      this.goToStep('users');
    }
  }

  // Check if we can proceed to final submission with enhanced validation
  canSubmit(): boolean {
    const hasValidClient =
      this.clientMode() === 'create' ? this.createClientForm.valid : this.selectedClient() !== null;
    const hasUsers = this.selectedDevelopers().length > 0 || this.selectedUserAdmins().length > 0;
    const noValidationErrors = this.validationErrors().length === 0;

    // Only require users when we're at the summary step
    const requiresUsers = this.currentStep() === 'summary';

    return (
      hasValidClient &&
      (!requiresUsers || hasUsers) &&
      noValidationErrors &&
      !this.isSubmitting() &&
      !this.isValidating()
    );
  }

  // Enhanced retry functionality
  async retrySubmission() {
    this.clearErrors();
    await this.submit();
  }

  // Get current submission progress percentage
  getProgressPercentage(): number {
    return this.submissionProgress();
  }

  // Get current step description for progress indicator
  getSubmissionStepDescription(): string {
    switch (this.submissionStep()) {
      case 'client':
        return 'Creating/validating client...';
      case 'permissions':
        return 'Creating user permissions...';
      case 'attributes':
        return 'Setting client attributes...';
      case 'complete':
        return 'Process completed successfully!';
      default:
        return 'Processing...';
    }
  }

  // Check if there are any errors to display
  hasErrors(): boolean {
    return (
      this.submissionError() !== null ||
      this.validationErrors().length > 0 ||
      this.networkError() !== null
    );
  }

  // Get all current errors for display
  getAllErrors(): string[] {
    const errors: string[] = [];

    if (this.submissionError()) {
      errors.push(this.submissionError()!);
    }

    if (this.networkError()) {
      errors.push(this.networkError()!);
    }

    errors.push(...this.validationErrors());

    return errors;
  }

  // Get client info for summary
  getClientSummary() {
    if (this.clientMode() === 'create') {
      return {
        mode: 'create' as const,
        clientId: this.createClientForm.get('clientId')?.value || '',
        name: this.createClientForm.get('name')?.value || '',
        description: this.createClientForm.get('description')?.value || '',
        protocol: this.createClientForm.get('protocol')?.value || 'openid-connect',
      };
    } else {
      return {
        mode: 'select' as const,
        client: this.selectedClient(),
      };
    }
  }

  // Edit functionality to go back to specific steps
  editClient() {
    this.goToStep('client');
  }

  editUsers() {
    this.goToStep('users');
  }

  // Enhanced submission with progress tracking and comprehensive error handling
  async submit() {
    // Pre-submission validation
    this.clearErrors();
    this.isValidating.set(true);

    const errors = this.validateForm();
    if (errors.length > 0) {
      this.validationErrors.set(errors);
      this.isValidating.set(false);
      return;
    }

    this.isValidating.set(false);
    this.isSubmitting.set(true);
    this.submissionProgress.set(0);

    try {
      let clientData: ClientRepresentation;

      // Step 1: Client Creation/Selection (20% progress)
      this.submissionStep.set('client');
      this.submissionProgress.set(20);

      if (this.clientMode() === 'create') {
        const newClientData: ClientRepresentation = {
          name: this.createClientForm.get('name')?.value?.trim() || '',
          clientId: this.createClientForm.get('clientId')?.value?.trim() || '',
          description: this.createClientForm.get('description')?.value?.trim() || '',
          protocol: this.createClientForm.get('protocol')?.value || 'openid-connect',
          enabled: true,
          publicClient: true,
          standardFlowEnabled: true,
          directAccessGrantsEnabled: false,
          serviceAccountsEnabled: false,
          authorizationServicesEnabled: false,
        };

        console.log('Creating new client:', newClientData);

        clientData = await this.handleApiCall(
          () => this.keycloakApi.createClient(newClientData),
          'Failed to create client'
        );

        // Fetch the created client details
        clientData = await this.handleApiCall(
          () => this.keycloakApi.getClientById(newClientData.clientId ?? ''),
          'Failed to fetch created client details'
        );

        console.log('Client created successfully:', clientData);
      } else {
        clientData = this.selectedClient()!;
        console.log('Using existing client:', clientData);
      }

      // Step 2: Group Creation and User Assignment (40% progress)
      this.submissionStep.set('permissions');
      this.submissionProgress.set(40);

      await this.handleApiCall(
        () => this.createUserPermissions(clientData),
        'Failed to create user permissions'
      );

      // Step 3: Client Attributes (60% progress)
      this.submissionStep.set('attributes');
      this.submissionProgress.set(60);

      // Step 4/5: Complete (100% progress)
      this.submissionStep.set('complete');
      this.submissionProgress.set(100);

      this.submissionSuccess.set(true);
      console.log('All phases completed successfully!');
      console.log('Client data:', clientData);
      console.log('Developers assigned:', this.selectedDevelopers());
      console.log('User admins assigned:', this.selectedUserAdmins());
    } catch (error) {
      console.error('Error during submission:', error);
      this.submissionError.set(
        this.networkError() ||
          (error instanceof Error ? error.message : 'An unknown error occurred during submission')
      );
    } finally {
      this.isSubmitting.set(false);
      this.isValidating.set(false);
    }
  }

  // Phase 6: Create groups and assign users
  private async createUserPermissions(clientData: ClientRepresentation) {
    const clientId = clientData.clientId || '';

    console.log('Phase 6: Creating user permissions for client:', clientId);
    await this.keycloakApi.saveClientPermissions(clientId, [
      ...this.selectedDevelopers().map((u) => ({ user: u.email!, permission: 'ia_full_admin' })),
      ...this.selectedUserAdmins().map((u) => ({ user: u.email!, permission: 'ia_users_admin' })),
    ]);

    console.log('User permissions assigned successfully');
  }
}
